import { useEffect, useState, useCallback } from "react";
import { api, fmtKg, fmtNum, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, Grains, FloppyDisk } from "@phosphor-icons/react";
import { PageLoading } from "@/components/layout/Layout";
import { useT } from "@/contexts/I18nContext";
import { ExportMenu } from "@/components/ExportMenu";

export default function Feed() {
    const { t } = useT();
    const [composition, setComposition] = useState(null);
    const [components, setComponents] = useState([{ _key: crypto.randomUUID(), name: "", weight_kg: "", dry_matter_pct: "" }]);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [evOpen, setEvOpen] = useState(false);
    const [evForm, setEvForm] = useState({ rfid: "", feed_kg: "", timestamp: "" });
    const [evSaving, setEvSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [c, ev] = await Promise.all([
                api.get("/feed/composition"),
                api.get("/feed/events", { params: { limit: 100 } }),
            ]);
            setComposition(c.data);
            if (c.data?.components?.length) {
                setComponents(c.data.components.map((x) => ({ ...x, _key: crypto.randomUUID(), weight_kg: String(x.weight_kg), dry_matter_pct: String(x.dry_matter_pct) })));
            }
            setEvents(ev.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const addComponent = () => setComponents([...components, { _key: crypto.randomUUID(), name: "", weight_kg: "", dry_matter_pct: "" }]);
    const removeComponent = (idx) => setComponents(components.filter((_, i) => i !== idx));
    const updateComponent = (idx, field, val) =>
        setComponents(components.map((c, i) => (i === idx ? { ...c, [field]: val } : c)));

    const saveComposition = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                components: components.map((c) => ({
                    name: c.name,
                    weight_kg: Number(c.weight_kg),
                    dry_matter_pct: Number(c.dry_matter_pct),
                })),
            };
            const r = await api.post("/feed/composition", payload);
            setComposition(r.data);
            toast.success(t("composition_saved"));
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const registerEvent = async (e) => {
        e.preventDefault();
        setEvSaving(true);
        try {
            await api.post("/feed/events", {
                rfid: evForm.rfid,
                feed_kg: Number(evForm.feed_kg),
                timestamp: evForm.timestamp ? new Date(evForm.timestamp).toISOString() : null,
            });
            toast.success(t("consumption_registered"));
            setEvOpen(false);
            setEvForm({ rfid: "", feed_kg: "", timestamp: "" });
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setEvSaving(false);
        }
    };

    if (loading) return <PageLoading />;

    const totalWeight = components.reduce((s, c) => s + (Number(c.weight_kg) || 0), 0);
    const totalDmKg = components.reduce((s, c) => s + (Number(c.weight_kg) || 0) * (Number(c.dry_matter_pct) || 0) / 100, 0);
    const frac = totalWeight > 0 ? (totalDmKg / totalWeight) : 0;

    return (
        <div className="space-y-8" data-testid="feed-page">
            <header>
                <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">{t("feed_title")}</h1>
                <p className="text-sm text-muted-foreground mt-1">{t("feed_subtitle")}</p>
            </header>

            <Card className="card-paper shadow-none">
                <div className="p-6 border-b border-[#DDE6D3] flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="flex items-center gap-2">
                            <Grains size={18} color="#4A6E00" weight="fill" />
                            <h2 className="font-display text-xl font-semibold">{t("active_composition")}</h2>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{t("active_composition_desc")}</p>
                    </div>
                    <div className="text-right">
                        <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">{t("total_dm")}</div>
                        <div className="metric-number text-2xl text-[#4A6E00]" data-testid="composition-dm-fraction">
                            {fmtNum(frac * 100, 2)}%
                        </div>
                    </div>
                </div>
                <form onSubmit={saveComposition} className="p-6 space-y-4">
                    <div className="space-y-3">
                        {components.map((c, idx) => (
                            <div key={c._key} className="grid grid-cols-12 gap-2 items-end" data-testid={`component-row-${idx}`}>
                                <div className="col-span-12 sm:col-span-5 space-y-1.5">
                                    <Label className="text-xs">{t("component")}</Label>
                                    <Input
                                        value={c.name}
                                        onChange={(e) => updateComponent(idx, "name", e.target.value)}
                                        required
                                        data-testid={`component-name-${idx}`}
                                    />
                                </div>
                                <div className="col-span-5 sm:col-span-3 space-y-1.5">
                                    <Label className="text-xs">{t("weight_kg")}</Label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        value={c.weight_kg}
                                        onChange={(e) => updateComponent(idx, "weight_kg", e.target.value)}
                                        required
                                        data-testid={`component-weight-${idx}`}
                                    />
                                </div>
                                <div className="col-span-5 sm:col-span-3 space-y-1.5">
                                    <Label className="text-xs">{t("dm_percent")}</Label>
                                    <Input
                                        type="number"
                                        step="0.1"
                                        min="0"
                                        max="100"
                                        value={c.dry_matter_pct}
                                        onChange={(e) => updateComponent(idx, "dry_matter_pct", e.target.value)}
                                        required
                                        data-testid={`component-dm-${idx}`}
                                    />
                                </div>
                                <div className="col-span-2 sm:col-span-1 flex justify-end">
                                    {components.length > 1 && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => removeComponent(idx)}
                                            className="text-[#C55D42]"
                                            data-testid={`remove-component-${idx}`}
                                        >
                                            <Trash size={16} />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-[#DDE6D3]">
                        <Button type="button" variant="outline" onClick={addComponent} data-testid="add-component-btn">
                            <Plus size={16} className="mr-2" /> {t("add_component")}
                        </Button>
                        <Button type="submit" disabled={saving} className="bg-[#083D1E] hover:bg-[#052A14] text-white" data-testid="save-composition-btn">
                            <FloppyDisk size={16} className="mr-2" />
                            {saving ? t("saving") : t("save_composition")}
                        </Button>
                    </div>
                </form>
            </Card>

            <Card className="card-paper shadow-none overflow-hidden">
                <div className="p-6 border-b border-[#DDE6D3] flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h2 className="font-display text-xl font-semibold">{t("feeder_consumption")}</h2>
                        <p className="text-xs text-muted-foreground mt-1">{t("feeder_consumption_desc")}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <ExportMenu endpoint="/export/feed" baseName="feed_events" testIdPrefix="feed-export" />
                        <Dialog open={evOpen} onOpenChange={setEvOpen}>
                            <DialogTrigger asChild>
                                <Button className="bg-[#083D1E] hover:bg-[#052A14] text-white" data-testid="new-feed-event-btn">
                                    <Plus size={16} className="mr-2" /> {t("register_consumption")}
                                </Button>
                            </DialogTrigger>
                        <DialogContent className="max-w-md">
                            <DialogHeader>
                                <DialogTitle className="font-display">{t("new_consumption")}</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={registerEvent} className="space-y-4">
                                <div className="space-y-1.5">
                                    <Label>{t("animal_rfid")}</Label>
                                    <Input
                                        required
                                        value={evForm.rfid}
                                        onChange={(e) => setEvForm({ ...evForm, rfid: e.target.value })}
                                        data-testid="feed-event-rfid-input"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label>{t("consumed_weight_kg")}</Label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        required
                                        value={evForm.feed_kg}
                                        onChange={(e) => setEvForm({ ...evForm, feed_kg: e.target.value })}
                                        data-testid="feed-event-kg-input"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label>{t("datetime_optional")}</Label>
                                    <Input
                                        type="datetime-local"
                                        value={evForm.timestamp}
                                        onChange={(e) => setEvForm({ ...evForm, timestamp: e.target.value })}
                                        data-testid="feed-event-ts-input"
                                    />
                                </div>
                                <DialogFooter>
                                    <Button type="submit" disabled={evSaving} className="bg-[#083D1E] hover:bg-[#052A14] text-white" data-testid="feed-event-save-btn">
                                        {evSaving ? t("saving") : t("register")}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                    </div>
                </div>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{t("date")}</TableHead>
                            <TableHead>{t("rfid")}</TableHead>
                            <TableHead>{t("origin")}</TableHead>
                            <TableHead className="text-right">{t("feed_as_fed")}</TableHead>
                            <TableHead className="text-right">{t("feed_dry_matter")}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {events.length === 0 ? (
                            <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">{t("no_consumption_registered")}</TableCell></TableRow>
                        ) : events.map((e) => (
                            <TableRow key={e.id}>
                                <TableCell>{fmtDate(e.timestamp)}</TableCell>
                                <TableCell className="font-medium">{e.rfid}</TableCell>
                                <TableCell>
                                    <Badge className={e.source === "feeder" ? "bg-[#083D1E] text-white hover:bg-[#083D1E]" : ""} variant={e.source === "feeder" ? "default" : "secondary"}>
                                        {e.source === "feeder" ? t("origin_feeder") : t("origin_manual")}
                                    </Badge>
                                </TableCell>
                                <TableCell className="text-right metric-number">{fmtKg(e.feed_kg)}</TableCell>
                                <TableCell className="text-right metric-number">{fmtKg(e.feed_dry_matter_kg)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Card>
        </div>
    );
}
